import { Component, OnInit } from '@angular/core';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { ToastrService } from 'ngx-toastr';
import { Constants, PropertyTypeDescription } from '../../constants.new';
import { IWeissmanModalComponent } from '../../Compliance/WeissmanModalService';
import { RestrictService, Roles } from '../../Common/Permissions/restrict.service';
import { MessageModalService } from '../../UI-Lib/Message-Box/messageModal.service';
import { EqualizationRuleService } from './equalization.rule.service';

export interface EqualizationRuleModalParams {
    assessorId: number;
    equalizationRule?: Core.EqualizationRuleModel;
}

export interface EqualizationRuleModalResult {
    equalizationRule: Core.EqualizationRuleModel;
    hasChanges: boolean;
    isDeleted: boolean;
}

@Component({
    selector: 'equalization-rule-modal',
    templateUrl: './equalization.rule.modal.component.html',
    standalone: false
})
export class EqualizationRuleModalComponent
    implements OnInit, IWeissmanModalComponent<EqualizationRuleModalParams, EqualizationRuleModalResult> {
    constructor(
        private readonly _bsModalRef: BsModalRef,
        private readonly _equalizationRuleService: EqualizationRuleService,
        private readonly _constants: Constants,
        private readonly _toastr: ToastrService,
        private readonly _restrictService: RestrictService,
        private readonly _messageModalService: MessageModalService
    ) {}

    params: EqualizationRuleModalParams;
    result: EqualizationRuleModalResult;
    propertyTypes: PropertyTypeDescription[] = [];
    model: Core.EqualizationRuleCreateModel;
    loading: boolean = false;
    canEdit: boolean = false;
    editMode: boolean = false;
    rule: Core.EqualizationRuleModel;

    private _originalModel: Core.EqualizationRuleCreateModel;

    ngOnInit(): void {
        this.propertyTypes = this._constants.propertyTypeArr();
        this.canEdit = this._restrictService.isInRole(Roles.JURISDICTIONSETUPS);
        this.rule = this.params.equalizationRule;
        this.editMode = !this.rule;
        this._buildModel();
    }

    get isDirty(): boolean {
        return this.editMode && JSON.stringify(this.model) !== JSON.stringify(this._originalModel);
    }

    get isInvalid(): boolean {
        return !!this.validate();
    }

    goToEditMode(): void {
        if (this.canEdit) {
            this.editMode = true;
        }
    }

    cancel(): void {
        if (this.rule) {
            this._buildModel();
            this.editMode = false;
        } else {
            this._bsModalRef.hide();
        }
    }

    async close(showWarning: boolean): Promise<void> {
        if (showWarning && this.isDirty) {
            try {
                await this._messageModalService.confirm('Editing is in progress. Are you sure you want to leave?');
            } catch {
                return;
            }
        }

        this._bsModalRef.hide();
    }

    async save(): Promise<void> {
        if (this.loading) {
            return;
        }

        if (this.isInvalid) {
            return;
        }

        this.loading = true;
        try {
            const saved = this.rule
                ? await this._equalizationRuleService.update({
                    equalizationRuleId: this.rule.equalizationRuleId,
                    rowVersion: this.rule.rowVersion,
                    factor: this.model.factor,
                    isActual: this.model.isActual,
                    automaticallyAdd: this.model.automaticallyAdd
                })
                : await this._equalizationRuleService.create(this.model);
            this.result = { equalizationRule: saved, hasChanges: true, isDeleted: false };
            this._bsModalRef.hide();
        } catch (error) {
            this._toastr.error(this._getErrorMessage(error, 'Unable to save Equalization Factor.'));
        } finally {
            this.loading = false;
        }
    }

    async delete(): Promise<void> {
        try {
            await this._messageModalService.confirm(
                `This will remove the system Equalization Factor row for this Property Type and Tax Year. If this row contains an Equalization Factor value, the Equalization Factor will be removed from synchronized Assessment Details. Overridden Assessment Details, or Assessment Details where the Equalization Factor is already absent, will not be changed. If this row's Equalization Factor is blank, no Assessment Details will be changed.`,
                'Confirm Delete'
            );
        } catch {
            return;
        }

        this.loading = true;
        try {
            await this._equalizationRuleService.delete(this.rule.equalizationRuleId, this.rule.rowVersion);
            this.result = { equalizationRule: null, hasChanges: false, isDeleted: true };
            this._bsModalRef.hide();
        } catch (error) {
            this._toastr.error(this._getErrorMessage(error, 'Unable to delete Equalization Factor.'));
        } finally {
            this.loading = false;
        }
    }

    private _buildModel(): void {
        this.model = this.rule
            ? {
                assessorId: this.rule.assessorId,
                propertyType: this.rule.propertyTypeId as Core.PropertyTypes,
                taxYear: this.rule.taxYear,
                factor: this.rule.factor,
                isActual: this.rule.isActual,
                automaticallyAdd: this.rule.automaticallyAdd
            }
            : {
                assessorId: this.params.assessorId,
                propertyType: null,
                taxYear: null,
                factor: null,
                isActual: null,
                automaticallyAdd: false
            };
        this._originalModel = { ...this.model };
    }

    private validate(): string {
        if (!this.editMode) {
            return null;
        }

        if (!this.model.propertyType) {
            return 'Property Type is required.';
        }
        if (!this.model.taxYear || !Number.isInteger(Number(this.model.taxYear)) || this.model.taxYear <= 2000) {
            return 'Tax Year must be greater than 2000.';
        }
        if (this.model.taxYear > 2050) {
            return 'Tax Year must not exceed 2050.';
        }
        if (this.model.isActual === null || this.model.isActual === undefined) {
            return 'Status is required.';
        }
        if (this.model.factor !== null && this.model.factor !== undefined) {
            if (Math.abs(Math.round(this.model.factor * 10000) - this.model.factor * 10000) > 1e-6) {
                return 'Eq. Factor must not have more than four decimal places.';
            }
            if (this.model.factor < 0.0001 || this.model.factor > 10) {
                return 'Eq. Factor must be between 0.0001 and 10.0000.';
            }
        } else if (this.model.automaticallyAdd) {
            return 'Automatically Add cannot be selected when Eq. Factor is blank.';
        }
        return null;
    }

    private _getErrorMessage(error: unknown, fallbackMessage: string): string {
        const err = error as { error?: { message?: string } } | null;
        return err?.error?.message || fallbackMessage;
    }
}
